import { useCallback, useState, useEffect } from 'react'
import { FileRejection } from 'react-dropzone'
import { Toaster } from '@ui'
import api, { IUploadedImageAsset } from '@api'
import find from 'lodash/find'
import remove from 'lodash/remove'
import debounce from 'lodash/debounce'
import findIndex from 'lodash/findIndex'
import { orderByDate, createRemoveById } from '../utils/assetListUtils'
import { transformFileToBase64 } from '@utils'
import { DateUtil } from '@utils/dateUtil'
import useProgress, { UploadingProgress } from './useProgress'
import { some } from 'lodash'

type useUploadSpaceImages = (
  spaceId: string,
  assets: IUploadedImageAsset[],
  updateDefaultImage: boolean,
  setUpdateDefaultImage: (value: boolean) => void
) => {
  uploadedImages: IUploadedImageAsset[]
  uploadImagesHandler: (files: File[], fileRejections: FileRejection[]) => Promise<void>
  deleteImagesHandler: (id: string) => void
  setImagesAsDefaultHandler: (id: string) => void
  progress: UploadingProgress
}

const useUploadSpaceImages: useUploadSpaceImages = (
  spaceId,
  assets,
  updateDefaultImage,
  setUpdateDefaultImage
) => {
  const [uploadedImages, setUploadedImages] = useState<IUploadedImageAsset[]>(assets)
  const { progress, resetProgress, setCurrentFilesCount, updateProgressPercent } = useProgress()

  const debouncedResetProgress = debounce(resetProgress, 5000)

  const setUploadedImagesWithOrdering = (assets: IUploadedImageAsset[]) => {
    const defaultImageItem = find(assets, { isDefault: true })
    const orderedAssets = orderByDate(assets)

    remove<IUploadedImageAsset>(orderedAssets, { isDefault: true })

    if (defaultImageItem) {
      orderedAssets.unshift(defaultImageItem)
    }

    setUploadedImages(orderedAssets)
  }

  const setImageListWithDefaultItem = useCallback(
    (defaultImage: IUploadedImageAsset, newImagesList: IUploadedImageAsset[]) => {
      const nowDate = DateUtil().fullDateWithTimeFormat()
      const images = [...uploadedImages]

      const previousDefaultImageIndex = findIndex(images, { isDefault: true })
      images[previousDefaultImageIndex].updatedAt = nowDate
      defaultImage.isDefault = true
      defaultImage.updatedAt = nowDate
      newImagesList = newImagesList.map((image) => ({ ...image, isDefault: false }))
      newImagesList = orderByDate(newImagesList)
      newImagesList.unshift(defaultImage)

      return newImagesList
    },
    [uploadedImages]
  )

  const getImageListWithUpdatedDefaultItem = useCallback(
    (id: string) => {
      const removeById = createRemoveById(id)
      const newImagesList = removeById([...uploadedImages])
      const images = [...uploadedImages]
      const defaultImage = find(images, { id })

      if (!defaultImage) {
        return newImagesList
      }

      return setImageListWithDefaultItem(defaultImage, newImagesList)
    },
    [setImageListWithDefaultItem, uploadedImages]
  )

  const setAssetsHandler = useCallback(async () => {
    try {
      const spaceAssets = await api.space.assets(spaceId)
      setUploadedImagesWithOrdering(spaceAssets)
    } catch (error: any) {
      Toaster.fail({ error })
    }
  }, [spaceId])

  const uploadImagesHandler = useCallback(
    async (files: File[]) => {
      try {
        updateProgressPercent({
          isUploading: true,
          totalFilesCount: files.length,
        })

        const shouldBeMarkAsDefault = !some(uploadedImages, { isDefault: true })
        await createSpaceAsset({
          files,
          spaceId,
          shouldBeMarkAsDefault,
          setCurrentFilesCount,
        })
        setUpdateDefaultImage(!updateDefaultImage)
        debouncedResetProgress()
      } catch (error: any) {
        Toaster.fail({ error })
        resetProgress()
      } finally {
        await setAssetsHandler()
      }
    },
    [
      updateProgressPercent,
      uploadedImages,
      spaceId,
      setCurrentFilesCount,
      debouncedResetProgress,
      resetProgress,
      setAssetsHandler,
      updateDefaultImage,
      setUpdateDefaultImage,
    ]
  )

  const deleteImagesHandler = useCallback(
    async (id: string) => {
      const images = [...uploadedImages]
      const removeById = createRemoveById(id)
      const withoutRemovedImage = removeById([...uploadedImages])
      const isImageDefault = find(images, { id })?.isDefault

      // mark the first element as default.
      if (isImageDefault && withoutRemovedImage.length) {
        withoutRemovedImage[0].isDefault = true
      }

      try {
        setUploadedImagesWithOrdering(withoutRemovedImage)
        await api.space.deleteSpaceAsset(id)
        await setAssetsHandler()
        setUpdateDefaultImage(!updateDefaultImage)
        Toaster.success({ message: 'You successfully deleted the image.' })
      } catch (error: any) {
        setUploadedImages(images)
        Toaster.fail({ error })
        await setAssetsHandler()
      }
    },
    [setAssetsHandler, uploadedImages, setUpdateDefaultImage, updateDefaultImage]
  )

  const setImagesAsDefaultHandler = useCallback(
    async (id: string) => {
      const newImagesList = getImageListWithUpdatedDefaultItem(id)

      try {
        setUploadedImages(newImagesList)
        await api.space.setDefaultSpaceAssets(id)
        await setAssetsHandler()
        setUpdateDefaultImage(!updateDefaultImage)
      } catch (error: any) {
        Toaster.fail({ error })
        await setAssetsHandler()
      }
    },
    [
      setAssetsHandler,
      getImageListWithUpdatedDefaultItem,
      updateDefaultImage,
      setUpdateDefaultImage,
    ]
  )

  useEffect(() => setUploadedImagesWithOrdering(assets), [assets])

  return {
    progress,
    setImagesAsDefaultHandler,
    deleteImagesHandler,
    uploadedImages,
    uploadImagesHandler,
  }
}

export default useUploadSpaceImages

type A = {
  files: File[]
  spaceId: string
  shouldBeMarkAsDefault: boolean
  setCurrentFilesCount: (currentFilesCount: number) => void
}
const createSpaceAsset = async ({
  files,
  spaceId,
  shouldBeMarkAsDefault,
  setCurrentFilesCount,
}: A): Promise<(void | IUploadedImageAsset)[]> => {
  let fileCount = 0

  return await Promise.all(
    files.map(async (file, i) => {
      const content = await transformFileToBase64(file)
      const result = await api.space
        .createSpaceAsset({
          content,
          fileName: file.name,
          isDefault: shouldBeMarkAsDefault && files.length === i + 1,
          space_id: spaceId,
        })
        .catch(() => {
          Toaster.fail({ message: `Something is wrong with the file ${file.name}` })
        })

      setCurrentFilesCount(++fileCount)
      return result
    })
  )
}
